import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import MobileLayout from '../../components/mobile/MobileLayout';

const companyName  = process.env.REACT_APP_COMPANY_NAME  || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
const API_BASE     = process.env.NODE_ENV==='development' ? '' : (process.env.REACT_APP_API_URL||'');

/* ─── Types ──────────────────────────────────────────────────────── */
interface Article { id: string; title: string; content: React.ReactNode; }
interface Section { id: string; title: string; icon: string; articles: Article[]; }

/* ─── Shared style atoms ─────────────────────────────────────────── */
const card: React.CSSProperties = {
  borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',
  boxShadow:'var(--card-shadow)',marginBottom:12,overflow:'hidden',
};
const primaryBtn: React.CSSProperties = {
  background:'#5B4FE8',color:'white',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,border:'none',width:'100%',cursor:'pointer',minHeight:44,
};
const ghostBtn: React.CSSProperties = {
  background:'var(--bg-element)',color:'var(--text-primary)',
  border:'1px solid var(--card-border)',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,cursor:'pointer',width:'100%',minHeight:44,
};
const sectionLabel: React.CSSProperties = {
  fontSize:11,color:'var(--text-faint)',textTransform:'uppercase',
  letterSpacing:'0.08em',marginBottom:6,fontWeight:700,display:'block',
};

/* ─── Article content helpers ────────────────────────────────────── */
function Para({ children }: { children: React.ReactNode }) {
  return <p style={{color:'var(--text-muted)',fontSize:14,lineHeight:1.7,margin:'0 0 12px'}}>{children}</p>;
}
function H3({ children }: { children: React.ReactNode }) {
  return <div style={{color:'var(--text-primary)',fontWeight:700,fontSize:15,margin:'16px 0 6px'}}>{children}</div>;
}
function Code({ children }: { children: React.ReactNode }) {
  return <pre style={{background:'var(--bg-element)',border:'1px solid var(--card-border)',borderRadius:8,padding:'12px 14px',fontFamily:'monospace',fontSize:12,color:'#818cf8',lineHeight:1.7,whiteSpace:'pre-wrap',overflowX:'auto',margin:'8px 0'}}>{children}</pre>;
}
function Tip({ children }: { children: React.ReactNode }) {
  return <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderLeft:'3px solid #f59e0b',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>{children}</div>;
}
function Warn({ children }: { children: React.ReactNode }) {
  return <div style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',borderLeft:'3px solid #ef4444',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>{children}</div>;
}
function Step({ n, title, desc }: { n: string; title: string; desc: React.ReactNode }) {
  return (
    <div style={{display:'flex',gap:12,alignItems:'flex-start',padding:14,background:'var(--bg-element)',borderRadius:10,marginBottom:10,border:'1px solid var(--card-border)'}}>
      <div style={{width:26,height:26,borderRadius:'50%',background:'#5B4FE8',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0}}>{n}</div>
      <div>
        <div style={{color:'var(--text-primary)',fontWeight:600,fontSize:14,marginBottom:3}}>{title}</div>
        <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.6}}>{desc}</div>
      </div>
    </div>
  );
}
function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  return <span style={{display:'inline-block',background:color+'20',color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,letterSpacing:'0.04em',marginLeft:6}}>{children}</span>;
}
function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" style={{color:'#818cf8',textDecoration:'underline',textUnderlineOffset:'2px'}}>{children} ↗</a>;
}

/* ─── Doc sections & articles ────────────────────────────────────── */
const DOC_SECTIONS: Section[] = [
  {
    id:'getting-started',title:'Getting Started',icon:'🚀',
    articles:[
      {
        id:'overview',title:'What is Autopilot?',
        content:(
          <>
            <Para>{companyName} lets any business set up an automated bot that chats with customers — on WhatsApp, their website, Facebook, or as a voice assistant. No coding required.</Para>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              {[
                {icon:'💬',label:'WhatsApp Bot',desc:'Chat with customers on WhatsApp 24/7'},
                {icon:'🌐',label:'Website Bot',desc:'Add a chat widget to your website'},
                {icon:'📘',label:'Facebook Bot',desc:'Auto-reply on your Facebook page'},
                {icon:'🎙️',label:'Voice Bot',desc:'AI voice assistant for calls'},
                {icon:'🤖',label:'AI Bot',desc:'Knowledge-base AI that answers naturally (Business+)'},
              ].map(c=>(
                <div key={c.label} style={{background:'var(--bg-element)',borderRadius:10,border:'1px solid var(--card-border)',padding:14}}>
                  <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
                  <div style={{color:'var(--text-primary)',fontWeight:600,fontSize:13,marginBottom:3}}>{c.label}</div>
                  <div style={{color:'var(--text-faint)',fontSize:12}}>{c.desc}</div>
                </div>
              ))}
            </div>
            <H3>How it works in 3 steps</H3>
            <Step n="1" title="Create a bot" desc="Pick the channel, fill in your business details, and choose an industry template to start from."/>
            <Step n="2" title="Build your flow" desc="Use the Flow Builder to set up triggers and responses. A trigger is a word customers type; a response is what your bot says back."/>
            <Step n="3" title="Go live" desc="For website bots, paste the embed code on your site. For WhatsApp, we register your number with Meta. That's it."/>
          </>
        ),
      },
      {
        id:'creating-bot',title:'Creating a Bot',
        content:(
          <>
            <Para>From the dashboard, click <strong>+ New Bot</strong> in the sidebar. You'll go through a 3-step wizard.</Para>
            <Step n="1" title="Choose your bot type" desc="Pick where your customers talk to you — WhatsApp, Website, Facebook, Voice, or AI Bot (Business plan+)."/>
            <Step n="2" title="Fill in your business details" desc="Business name, industry, welcome message, and channel-specific info."/>
            <Step n="3" title="Pick a template" desc="Choose from industry templates to start with pre-built conversation flows."/>
            <H3>Industry templates</H3>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {['💈 Barber Shop','💅 Beauty Salon','🍕 Restaurant','👕 Clothing Store','🏠 Real Estate','🎨 Custom'].map(t=>(
                <span key={t} style={{background:'var(--bg-element)',border:'1px solid var(--card-border)',borderRadius:8,padding:'6px 12px',fontSize:13,color:'var(--text-secondary)'}}>{t}</span>
              ))}
            </div>
          </>
        ),
      },
      {
        id:'plans',title:'Plans & Pricing',
        content:(
          <>
            <Para>All paid plans include a one-time setup fee. No long-term contracts — cancel any time.</Para>
            {[
              {plan:'Free',price:'$0',setup:'$0',bots:'1 bot (website only)',convs:'100/month',support:'Community'},
              {plan:'Starter',price:'$45/mo · $432/yr',setup:'$25',bots:'1 bot (any channel)',convs:'500/month',support:'Email'},
              {plan:'Business',price:'$75/mo · $720/yr',setup:'$50',bots:'3 bots',convs:'2,000/month',support:'Priority'},
              {plan:'Agency',price:'$200/mo · $1,920/yr',setup:'$100',bots:'10 bots',convs:'10,000/month',support:'Dedicated'},
              {plan:'Enterprise',price:'Contact us',setup:'—',bots:'Unlimited',convs:'Unlimited',support:'Dedicated manager'},
            ].map(r=>(
              <div key={r.plan} style={{background:'var(--bg-element)',borderRadius:10,border:'1px solid var(--card-border)',padding:14,marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>{r.plan} <span style={{color:'#5B4FE8'}}>{r.price}</span></div>
                <div style={{display:'flex',gap:16,fontSize:13,color:'var(--text-muted)',flexWrap:'wrap'}}>
                  <span>🤖 {r.bots}</span>
                  <span>💬 {r.convs}</span>
                  <span>📧 {r.support}</span>
                  <span>🔧 Setup: {r.setup}</span>
                </div>
              </div>
            ))}
            <Tip>💡 AI Bots require Business plan or above. Upgrade any time from the Billing page in your dashboard.</Tip>
          </>
        ),
      },
    ],
  },
  {
    id:'flow-builder',title:'Flow Builder',icon:'🔀',
    articles:[
      {
        id:'flow-overview',title:'What is the Flow Builder?',
        content:(
          <>
            <Para>The Flow Builder is where you build conversations. When a customer types a word (<strong style={{color:'#818cf8'}}>trigger</strong>), your bot sends a sequence of replies (<strong style={{color:'#818cf8'}}>responses</strong>).</Para>
            <H3>Triggers</H3>
            <Para>A trigger is a keyword that starts a conversation flow. When a customer's message contains that word, the bot runs that flow.</Para>
            <Code>{`Trigger: "pricing"
→ Matched when customer types: "what's the pricing?",
  "pricing please", "I need pricing info"

Trigger: "hi"
→ Matched when customer types: "hi", "hello", "hey"`}</Code>
            <Warn>⚠️ Keep triggers specific. "our" appears in many sentences and would match unintentionally.</Warn>
            <H3>Responses</H3>
            <Para>Each trigger can have multiple responses sent in order. You can add, reorder (↑), and delete responses.</Para>
          </>
        ),
      },
      {
        id:'response-types',title:'Response Types (WhatsApp)',
        content:(
          <>
            {[
              {icon:'💬',name:'Text',desc:'Plain message. Supports WhatsApp markdown: *bold*, _italic_, ~strikethrough~. Use {{variableName}} to insert collected answers.'},
              {icon:'🖼️',name:'Image',desc:'Sends an image. Provide a public HTTPS URL. Add an optional caption.'},
              {icon:'📄',name:'Document',desc:'Sends a PDF, Word, or other file. Provide a public HTTPS URL and filename.'},
              {icon:'🔘',name:'Buttons',desc:"Up to 3 clickable buttons. When clicked, the button's payload is sent as a message (matching another trigger)."},
              {icon:'📋',name:'List',desc:'A scrollable list with sections and rows. Each row has a title (max 24 chars) and payload. Best for menus with 4+ options.'},
              {icon:'❓',name:'Ask',desc:'Bot asks a question, waits for reply, saves the answer as a variable. Use {{variableName}} in later Text responses.'},
              {icon:'🔗',name:'Chain',desc:'After sending this, the bot auto-sends a trigger word after a delay (in seconds). Useful for guided flows.'},
              {icon:'🌐',name:'Webhook',desc:'Calls an external API URL. Returns a value you can show in the response.'},
              {icon:'📡',name:'Dynamic List',desc:'Fetches items from your API and shows them as a WhatsApp list. Items update dynamically.'},
              {icon:'🛒',name:'Order Handler',desc:'Complete order flow: collects customer info, quantity, then generates payment link / notifies admin / calls API.'},
            ].map(item=>(
              <div key={item.name} style={{display:'flex',gap:12,padding:12,background:'var(--bg-element)',borderRadius:10,border:'1px solid var(--card-border)',marginBottom:8}}>
                <span style={{fontSize:20,flexShrink:0}}>{item.icon}</span>
                <div>
                  <div style={{color:'var(--text-primary)',fontWeight:600,fontSize:14,marginBottom:3}}>{item.name}</div>
                  <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.6}}>{item.desc}</div>
                </div>
              </div>
            ))}
          </>
        ),
      },
      {
        id:'templates',title:'Templates',
        content:(
          <>
            <Para>Templates are pre-built conversation flows. They're tagged by bot type so the right templates appear in the right builder.</Para>
            <H3>Loading a template</H3>
            <Para>Inside any Flow Builder, click <strong>📋 Load template</strong> in the top bar. A panel opens showing templates for that bot type. Click one, confirm, and the flow is loaded.</Para>
            <Warn>⚠️ Loading a template overwrites your current flow. Save first if you want to keep it.</Warn>
            <H3>Starter templates</H3>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {[
                {icon:'💈',name:'Barber Shop',color:'#10b981'},
                {icon:'💅',name:'Beauty Salon',color:'#10b981'},
                {icon:'🍕',name:'Restaurant',color:'#10b981'},
                {icon:'👕',name:'Clothing Store',color:'#10b981'},
                {icon:'🏠',name:'Real Estate',color:'#10b981'},
                {icon:'🏢',name:'Business Website',color:'#3b82f6'},
                {icon:'📞',name:'Business IVR',color:'#8b5cf6'},
              ].map(t=>(
                <div key={t.name} style={{background:'var(--bg-element)',border:`1px solid ${t.color}30`,borderRadius:9,padding:'8px 12px',display:'flex',gap:8,alignItems:'center'}}>
                  <span style={{fontSize:18}}>{t.icon}</span>
                  <span style={{fontSize:13,color:'var(--text-secondary)',fontWeight:600}}>{t.name}</span>
                </div>
              ))}
            </div>
          </>
        ),
      },
    ],
  },
  {
    id:'whatsapp',title:'WhatsApp Bot',icon:'💬',
    articles:[
      {
        id:'whatsapp-setup',title:'WhatsApp Setup',
        content:(
          <>
            <Para>When you create a WhatsApp bot, we submit your phone number to Meta for WhatsApp Business API access. Here's what you need:</Para>
            {['✅ A phone number NOT already on personal WhatsApp','✅ A business name and address (for Meta verification)','✅ A business email (for setup updates)','✅ 1–3 business days for Meta approval'].map(r=>(
              <div key={r} style={{color:'var(--text-secondary)',fontSize:14,marginBottom:8}}>{r}</div>
            ))}
            <Tip>💡 Use a dedicated SIM card or landline for your WhatsApp bot number. Once registered to the Business API, it cannot be used on the WhatsApp consumer app.</Tip>
            <H3>Webhook setup (after approval)</H3>
            <Step n="1" title="Open Bot Settings" desc="In the dashboard, select your WhatsApp bot and click Settings."/>
            <Step n="2" title="Copy the Webhook URL" desc="The settings modal shows your unique Webhook URL and Verify Token."/>
            <Step n="3" title="Go to your Meta App Dashboard" desc={<>Open <ExtLink href="https://developers.facebook.com/apps">developers.facebook.com/apps</ExtLink> → your App → WhatsApp → Configuration. Full guide: <ExtLink href="https://developers.facebook.com/docs/whatsapp/cloud-api/guides/set-up-webhooks/">Meta's webhook docs</ExtLink>.</>}/>
            <Step n="4" title="Add the webhook" desc="Paste the Webhook URL, enter the Verify Token (shown as BusinessName_<year> in Bot Settings — copy it exactly), and subscribe to the 'messages' field."/>
            <Step n="5" title="Save your credentials" desc="Back in Settings, enter your Phone Number ID and Access Token from Meta Dashboard."/>
            <Warn>⚠️ The "Temporary access token" from the App Dashboard quick-start expires in <strong>24 hours</strong> — fine for testing only. For a token that doesn't expire, create a <strong>System User</strong> in <ExtLink href="https://business.facebook.com/settings/system-users">Business Settings → System Users</ExtLink> and generate a permanent token for it. Guide: <ExtLink href="https://developers.facebook.com/docs/business-management-apis/system-users/install-apps-and-generate-tokens/">Meta's System User docs</ExtLink>.</Warn>
          </>
        ),
      },
      {
        id:'whatsapp-markdown',title:'WhatsApp Markdown',
        content:(
          <>
            <Para>You can use WhatsApp's built-in formatting in your Text responses:</Para>
            <Code>{`*bold text*
_italic text_
~strikethrough~
\`\`\`monospace\`\`\`

Line breaks: just press Enter in the text field`}</Code>
          </>
        ),
      },
      {
        id:'campaigns',title:'Campaigns',
        content:(
          <>
            <Para>Campaigns let you send bulk messages to all your WhatsApp contacts or a custom contact list. Available on Starter plan and above.</Para>
            <H3>Schedule</H3>
            <Para>Set which days of the week and what time to send. The scheduler runs every 5 minutes.</Para>
            <H3>Contact lists</H3>
            <Para>Create custom contact lists from the Campaigns page to target specific groups. You can import contacts via CSV (Name, Phone, Email format).</Para>
            <Tip>💡 Set campaign status to Active for it to run automatically on the selected schedule.</Tip>
          </>
        ),
      },
    ],
  },
  {
    id:'facebook',title:'Facebook Bot',icon:'📘',
    articles:[
      {
        id:'facebook-setup',title:'Facebook Setup',
        content:(
          <>
            <Para>The Facebook bot replies to messages sent to your Facebook business page, using your Page ID and Page Access Token.</Para>
            {['✅ Admin access to the Facebook Page','✅ Page Access Token with pages_messaging permission','✅ Page must be published (not draft)'].map(r=>(
              <div key={r} style={{color:'var(--text-secondary)',fontSize:14,marginBottom:8}}>{r}</div>
            ))}
            <H3>Getting your credentials</H3>
            <Step n="1" title="Go to the Meta App Dashboard" desc={<>Open <ExtLink href="https://developers.facebook.com/apps">developers.facebook.com/apps</ExtLink> and log in with the account that manages your page.</>}/>
            <Step n="2" title="Create or open your app" desc="Create a Business app if you don't have one. Add the Messenger product."/>
            <Step n="3" title="Find your Page ID" desc="Go to your Facebook Page → About → scroll to find Page ID."/>
            <Step n="4" title="Generate a permanent token" desc={<>Use a <strong>System User</strong> for a token that doesn't expire: <ExtLink href="https://business.facebook.com/settings/system-users">Business Settings → System Users</ExtLink> → Add → assign your app/Page → Generate New Token with pages_messaging. Guide: <ExtLink href="https://developers.facebook.com/docs/business-management-apis/system-users/install-apps-and-generate-tokens/">Meta's System User docs</ExtLink>.</>}/>
            <Step n="5" title="Enter in Bot Settings" desc="Paste both the Page ID and Page Access Token."/>
          </>
        ),
      },
    ],
  },
  {
    id:'voice',title:'Voice Bot',icon:'🎙️',
    articles:[
      {
        id:'voice-setup',title:'Voice Bot Overview',
        content:(
          <>
            <Para>The voice bot is an AI voice assistant embedded on your website. Visitors speak to it and it responds with synthesised speech, running the flow you build in the Voice Flow Builder.</Para>
            <Tip>💡 Write scripts as natural spoken sentences — no bullet points or markdown, it's read aloud exactly as written.</Tip>
            <H3>Step types</H3>
            {[
              {icon:'🔊',n:'Say',d:'The bot speaks this text. Use {{variable}} to insert collected answers.'},
              {icon:'❓',n:'Ask',d:'The bot asks a question and saves the spoken answer as a variable.'},
              {icon:'🔀',n:'Branch',d:'IVR menu — bot listens then routes to a different trigger based on what the caller says.'},
              {icon:'📞',n:'Transfer',d:'Bot says a message then transfers the caller to a phone number.'},
              {icon:'📴',n:'End call',d:'Bot says goodbye and hangs up.'},
            ].map(item=>(
              <div key={item.n} style={{display:'flex',gap:12,padding:12,background:'var(--bg-element)',borderRadius:10,border:'1px solid var(--card-border)',marginBottom:8}}>
                <span style={{fontSize:18,flexShrink:0}}>{item.icon}</span>
                <div>
                  <div style={{color:'var(--text-primary)',fontWeight:600,fontSize:14,marginBottom:3}}>{item.n}</div>
                  <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.6}}>{item.d}</div>
                </div>
              </div>
            ))}
            <H3>Language &amp; voice</H3>
            <Para>Select Language (English US/UK/ZW, Shona, Ndebele) and Voice type (Woman/Man) in the Voice Flow Builder top bar.</Para>
          </>
        ),
      },
    ],
  },
  {
    id:'ai-bot',title:'AI Bot',icon:'🤖',
    articles:[
      {
        id:'ai-overview',title:'AI Bot Overview',
        content:(
          <>
            <Para>An AI Bot replaces fixed triggers/responses with an AI model (Groq) that answers questions in natural language, using only a knowledge base you write. Available on WhatsApp, Website, or Facebook.</Para>
            <Warn>⚠️ Requires the Business plan or above. There's no Flow Builder for AI Bots — just a knowledge base to fill in.</Warn>
            <H3>Setup</H3>
            <Step n="1" title="Create the bot" desc='Choose "AI Bot" in + New Bot, then pick a channel.'/>
            <Step n="2" title="Get a free Groq API key" desc="Groq hosts the AI model. Sign up free — no credit card needed."/>
            <Step n="3" title="Paste the key in" desc='Paste your Groq API key into the "AI API Key" field.'/>
            <Step n="4" title="Add channel credentials" desc="WhatsApp/Facebook AI bots need the same credentials as regular bots — see those sections."/>
            <Step n="5" title="Write your Knowledge Base" desc="In Bot Settings, paste pricing, FAQs, policies, hours. The AI only answers from this text."/>
            <Step n="6" title="Pick a Tone" desc="Choose how the bot should sound — see Tone presets below."/>
            <Tip>💡 Get your free key at <ExtLink href="https://console.groq.com/keys">console.groq.com/keys</ExtLink>. See Groq's <ExtLink href="https://console.groq.com/docs/rate-limits">rate limits</ExtLink> if you send high volumes.</Tip>
          </>
        ),
      },
      {
        id:'ai-tone',title:'Tone Presets',
        content:(
          <>
            <Para>Set in Bot Settings → Tone. Changes how the bot phrases replies — not what facts it can use.</Para>
            {[
              {n:'Friendly & Casual',d:'Default. Warm, conversational. No sales pressure.'},
              {n:'Marketer',d:'Sells the value/benefit. Ends with a relevant follow-up question.'},
              {n:'Sales-driven',d:'Never dead-ends — always offers a next step (quote, booking, contact) and asks qualifying questions.'},
              {n:'Straight to the Point',d:'Brief and direct. No pitch, no follow-up.'},
            ].map(t=>(
              <div key={t.n} style={{background:'var(--bg-element)',borderRadius:10,border:'1px solid var(--card-border)',padding:14,marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>{t.n}</div>
                <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.6}}>{t.d}</div>
              </div>
            ))}
          </>
        ),
      },
      {
        id:'ai-knowledge-base',title:'Writing a Knowledge Base',
        content:(
          <>
            <Para>The AI only knows what's in this text box — if it's not there, the bot tells the customer to contact you directly rather than guessing.</Para>
            {[
              'Use clear ALL-CAPS section headers (e.g. "PRICING", "FAQ") — large knowledge bases are split by these headers so the bot pulls in the most relevant section per question.',
              'Be specific: exact prices, locations, hours, policies — not vague summaries.',
              'Large knowledge bases (8,000+ characters) are fine — only the most relevant section is sent per reply.',
            ].map(r=><div key={r} style={{color:'var(--text-secondary)',fontSize:13,lineHeight:1.6,marginBottom:6}}>• {r}</div>)}
            <Code>{`================================
PRICING
================================
Static Billboard — Harare CBD: $450-$500/month

================================
FAQ
================================
Q: Do you offer discounts?
A: Yes, contact our team.`}</Code>
            <Tip>💡 Use "Custom System Instructions" for behaviour rules that aren't facts — e.g. "Always reply in Shona."</Tip>
          </>
        ),
      },
    ],
  },
  {
    id:'website-bot',title:'Website Bot',icon:'🌐',
    articles:[
      {
        id:'website-setup',title:'Installing the Widget',
        content:(
          <>
            <Para>The website bot adds a chat bubble to your site. Visitors click it to chat.</Para>
            <Step n="1" title="Get your embed code" desc="In the dashboard, select your website bot, go to the Embed Code tab, and copy the script tag."/>
            <Step n="2" title="Paste it on your site" desc="Open your website's HTML editor. Paste the script just before the closing </body> tag."/>
            <Step n="3" title="Publish and test" desc="Save and publish your site. The chat bubble should appear in the bottom-right corner."/>
            <Code>{`<!-- Basic -->
<script src="${API_BASE}/widget.js" data-bot-id="YOUR_BOT_ID"></script>

<!-- With options -->
<script
  src="${API_BASE}/widget.js"
  data-bot-id="YOUR_BOT_ID"
  data-primary-color="#5B4FE8"
  data-position="bottom-right"
></script>`}</Code>
          </>
        ),
      },
    ],
  },
  {
    id:'advanced',title:'Advanced',icon:'📡',
    articles:[
      {
        id:'dynamic-lists',title:'Dynamic Lists & Responses',
        content:(
          <>
            <H3>Dynamic Lists</H3>
            <Para>A Dynamic List fetches items from your API and shows them as a WhatsApp list. Perfect for product catalogues, service menus, or any data that changes often.</Para>
            <Code>{`// Your API must return JSON like:
{
  "data": [
    { "id": "prod_001", "name": "Basic Website — $500" },
    { "id": "prod_002", "name": "E-commerce Store — $1,200" }
  ]
}
// Title path: name
// Payload path: id`}</Code>
            <H3>Dynamic Responses</H3>
            <Para>When a user selects from a Dynamic List, a Dynamic Response fetches and displays full details — image, title, price, description, and action buttons.</Para>
            <Code>{`// API URL: https://api.yoursite.com/product/{{id}}
// Title path: data.name
// Meta path: data.price  →  Meta label: Price
// Description path: data.description
// Image path: data.image_url`}</Code>
            <Tip>💡 Use {'{{id}}'} in the API URL and button payloads. It gets replaced with the actual ID the user selected from the list.</Tip>
          </>
        ),
      },
      {
        id:'order-handler',title:'Order Handler',
        content:(
          <>
            <Para>The Order Handler is a complete order-processing flow. It collects customer details, asks for quantity, calculates totals, and then takes one of four actions.</Para>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              {[
                {icon:'💰',label:'Payment link',desc:'Generates a link with order details as URL params.'},
                {icon:'📲',label:'Notify admin',desc:'Sends order details to your WhatsApp number.'},
                {icon:'🌐',label:'Call API',desc:'Posts order data to your backend system.'},
                {icon:'💬',label:'Custom message',desc:'Sends a custom confirmation message.'},
              ].map(a=>(
                <div key={a.label} style={{background:'var(--bg-element)',border:'1px solid var(--card-border)',borderRadius:9,padding:12}}>
                  <div style={{fontSize:18,marginBottom:6}}>{a.icon}</div>
                  <div style={{color:'var(--text-primary)',fontWeight:600,fontSize:13,marginBottom:3}}>{a.label}</div>
                  <div style={{color:'var(--text-faint)',fontSize:12}}>{a.desc}</div>
                </div>
              ))}
            </div>
            <H3>Available variables</H3>
            <Code>{`{{id}}               — Product ID from the Dynamic List
{{price}}            — Product price
{{quantity}}         — Quantity the customer ordered
{{total}}            — price × quantity (auto-calculated)
{{customer_name}}    — Collected name
{{customer_phone}}   — Collected phone
{{customer_address}} — Collected delivery address`}</Code>
          </>
        ),
      },
      {
        id:'webhooks',title:'Webhooks & API',
        content:(
          <>
            <Para>The Webhook response calls any external API when triggered. Use it to check order status, fetch data, or trigger actions in your system.</Para>
            <Code>{`// Check order status
API URL: https://api.yoursite.com/order/{{message}}
Method: GET
Response path: data.status
Success message: "Your order status is: {{response}}"

// POST with authentication
API URL: https://api.yoursite.com/lead
Method: POST
Body: {"phone":"{{phone}}","message":"{{message}}"}`}</Code>
          </>
        ),
      },
    ],
  },
  {
    id:'help',title:'Help',icon:'🔧',
    articles:[
      {
        id:'troubleshooting',title:'Troubleshooting',
        content:(
          <>
            {[
              {prob:"Bot doesn't respond at all (WhatsApp)",sol:'Check that your webhook is verified in Meta Dashboard. Go to Bot Settings → copy the Webhook URL → paste it in Meta Console → WhatsApp → Configuration → Webhook. Make sure you\'ve subscribed to "messages".'},
              {prob:'"Invalid access token" error',sol:'If you used the quick-start "Temporary access token," it expires after 24 hours — that\'s expected. Generate a permanent token via a System User in Business Settings instead — see the WhatsApp Bot section for the link and steps.'},
              {prob:'Webhook verification fails',sol:'Create the bot in Autopilot first, then copy the webhook URL. The bot must exist before you can verify. Also check your Verify Token exactly matches the BusinessName_<year> value shown in Bot Settings — copy it rather than retyping it.'},
              {prob:'Bot responds with the wrong trigger',sol:'Triggers match ANY message containing that word. Make triggers specific (e.g. "opening_hours" not "hours"). Longer, more specific triggers are safer.'},
              {prob:'Images not sending',sol:'Image URL must be HTTPS and publicly accessible. Test by opening the URL in your browser — if it shows the image without login, it works.'},
              {prob:'{{variables}} not being replaced',sol:"Variables are only available after an Ask response stores them. Make sure the variable name in the Ask field exactly matches what you're using in {{}} — they are case-sensitive."},
              {prob:'Website widget not appearing',sol:'Make sure the script tag is placed before the closing </body> tag. Check that your domain is not in the restricted list. Open browser console for errors.'},
              {prob:'Facebook bot not replying',sol:"Make sure you've set up a webhook in Meta Console pointing to your Autopilot backend. The Page Access Token must have pages_messaging permission — see the Facebook Bot section for the System User token guide."},
              {prob:'AI bot keeps saying "I don\'t have that information"',sol:'The AI only answers from the Knowledge Base text in Bot Settings — it never guesses. Add the missing fact there, organised under a clear header.'},
            ].map(item=>(
              <div key={item.prob} style={{borderRadius:10,overflow:'hidden',border:'1px solid var(--card-border)',marginBottom:10}}>
                <div style={{background:'rgba(245,158,11,0.08)',borderBottom:'1px solid rgba(245,158,11,0.2)',padding:'10px 14px'}}>
                  <span style={{color:'#f59e0b',fontWeight:600,fontSize:13}}>⚠️ {item.prob}</span>
                </div>
                <div style={{padding:'10px 14px',background:'rgba(16,185,129,0.06)'}}>
                  <span style={{color:'#10b981',fontSize:13}}>✅ {item.sol}</span>
                </div>
              </div>
            ))}
          </>
        ),
      },
      {
        id:'faq',title:'FAQ',
        content:(
          <>
            {[
              {q:'Do I need coding skills?',a:'No. The Flow Builder is visual — you type, click, and configure. No code required for any bot type.'},
              {q:'Can I have multiple bots?',a:'Yes. Free: 1 website bot. Starter: 1 bot (any channel). Business: 3 bots. Agency: 10 bots. Enterprise: unlimited.'},
              {q:'What\'s the difference between a regular bot and an AI Bot?',a:'Regular bots match keyword triggers in the Flow Builder. AI Bots (Business plan+) use a Groq-powered AI that answers naturally from a knowledge base you write — no triggers to configure.'},
              {q:'Can I use my personal WhatsApp number?',a:"No. Each phone number can only have one WhatsApp account. You'll need a separate SIM or landline for the bot."},
              {q:'How long does WhatsApp setup take?',a:'1–3 business days for Meta to verify and approve your number.'},
              {q:'Can customers talk to a human?',a:'Yes. Use the "Hand off to human" response type (website bot) or add a trigger that tells customers to call/email you directly.'},
              {q:'Can I change my bot later?',a:'Yes. The Flow Builder is live-editable. Save changes and they take effect immediately.'},
              {q:'What happens when a message doesn\'t match any trigger?',a:'For WhatsApp bots, the bot sends nothing (or you can add a fallback trigger). For website bots, the "offline/no-match message" is shown.'},
              {q:'Can I export conversation data?',a:'Yes. Go to the Conversations tab → Export CSV. You can also export daily report data from the Reports tab.'},
              {q:'Is my data secure?',a:'Yes. Data is stored in Firebase with authentication rules. Access tokens are stored encrypted. We never share your conversation data.'},
              {q:`How do I contact ${companyName} support?`,a:`Email ${companyEmail} or use the Support link in the sidebar. Business and Agency plans get priority response times.`},
            ].map((item,i)=>(
              <div key={i} style={{paddingBottom:16,marginBottom:16,borderBottom:'1px solid var(--card-border)'}}>
                <div style={{color:'#818cf8',fontWeight:600,fontSize:14,marginBottom:6}}>❓ {item.q}</div>
                <Para>{item.a}</Para>
              </div>
            ))}
          </>
        ),
      },
    ],
  },
];

/* ════════════════════════════════════════════════════════════════ */
export default function DocumentationMobile() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery]   = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['getting-started']));
  const [activeArticle, setActiveArticle] = useState<Article|null>(null);
  const [visitedIds, setVisitedIds]     = useState<Set<string>>(new Set());
  const [helpful, setHelpful]           = useState<'up'|'down'|null>(null);

  useEffect(()=>{ auth.onAuthStateChanged(()=>{}); },[]);

  /* Search: flatten all articles */
  const allArticles = useMemo(()=>
    DOC_SECTIONS.flatMap(s=>s.articles.map(a=>({...a,sectionId:s.id,sectionTitle:s.title,sectionIcon:s.icon}))),
  []);

  const searchResults = useMemo(()=>{
    if(!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allArticles.filter(a=>
      a.title.toLowerCase().includes(q) || a.sectionTitle.toLowerCase().includes(q)
    );
  },[searchQuery,allArticles]);

  const toggleSection=(id:string)=>{
    setExpandedSections(prev=>{
      const next=new Set(prev);
      if(next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openArticle=(article:Article)=>{
    setActiveArticle(article);
    setHelpful(null);
    setVisitedIds(prev=>new Set(prev).add(article.id));
    setSearchQuery('');
  };

  /* ── Article view ── */
  if(activeArticle){
    return(
      <MobileLayout
        title={activeArticle.title}
        onBack={()=>setActiveArticle(null)}
        hideNav={false}
      >
        <div style={{padding:16}}>
          {/* Article content */}
          {activeArticle.content}

          {/* Helpful? */}
          <div style={{marginTop:24,borderTop:'1px solid var(--card-border)',paddingTop:16,textAlign:'center'}}>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:12}}>Was this article helpful?</div>
            <div style={{display:'flex',gap:12,justifyContent:'center'}}>
              <button
                onClick={()=>setHelpful('up')}
                style={{
                  padding:'8px 20px',borderRadius:9,border:`1px solid ${helpful==='up'?'#10b981':'var(--card-border)'}`,
                  background:helpful==='up'?'rgba(16,185,129,0.15)':'var(--bg-element)',
                  color:helpful==='up'?'#10b981':'var(--text-muted)',cursor:'pointer',fontSize:16,fontWeight:600,minHeight:44,
                }}
              >👍</button>
              <button
                onClick={()=>setHelpful('down')}
                style={{
                  padding:'8px 20px',borderRadius:9,border:`1px solid ${helpful==='down'?'#ef4444':'var(--card-border)'}`,
                  background:helpful==='down'?'rgba(239,68,68,0.1)':'var(--bg-element)',
                  color:helpful==='down'?'#ef4444':'var(--text-muted)',cursor:'pointer',fontSize:16,fontWeight:600,minHeight:44,
                }}
              >👎</button>
            </div>
            {helpful && (
              <div style={{marginTop:12,fontSize:13,color:helpful==='up'?'#10b981':'var(--text-muted)'}}>
                {helpful==='up'?'Glad it helped!':'Thanks for the feedback. Contact support if you need more help.'}
              </div>
            )}
          </div>

          {/* Still need help */}
          <div style={{marginTop:20,borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',padding:16,textAlign:'center'}}>
            <div style={{fontSize:28,marginBottom:8}}>🆘</div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>Still need help?</div>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16,lineHeight:1.6}}>
              Our team is happy to help you get your bot set up.
            </p>
            <a href={`mailto:${companyEmail}`} style={{...primaryBtn,display:'block',textDecoration:'none',marginBottom:10,boxSizing:'border-box'}}>
              ✉️ Email support
            </a>
            <button onClick={()=>navigate('/billing')} style={ghostBtn}>💳 Upgrade plan</button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  /* ── Main doc list view ── */
  return(
    <MobileLayout title="Documentation">
      <div style={{padding:16}}>
        {/* Search */}
        <div style={{position:'relative',marginBottom:16}}>
          <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:16,pointerEvents:'none'}}>🔍</div>
          <input
            value={searchQuery}
            onChange={e=>setSearchQuery(e.target.value)}
            placeholder="Search docs..."
            style={{
              background:'var(--bg-input)',border:'1px solid var(--card-border)',borderRadius:10,
              padding:'10px 14px 10px 38px',color:'var(--text-primary)',fontSize:14,
              width:'100%',boxSizing:'border-box',outline:'none',
            }}
          />
          {searchQuery && (
            <button
              onClick={()=>setSearchQuery('')}
              style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:18,padding:4}}
            >✕</button>
          )}
        </div>

        {/* Search results */}
        {searchQuery.trim() && (
          <>
            {searchResults.length===0 ? (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:14,padding:'24px 0'}}>
                No results for "{searchQuery}"
              </div>
            ):(
              <>
                <div style={sectionLabel}>{searchResults.length} result{searchResults.length!==1?'s':''}</div>
                {searchResults.map(a=>(
                  <div key={a.id} onClick={()=>openArticle(a)} style={{
                    borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',
                    boxShadow:'var(--card-shadow)',marginBottom:10,padding:14,cursor:'pointer',
                    borderLeft:`3px solid #5B4FE8`,
                  }}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{a.title}</div>
                    <div style={{fontSize:12,color:'var(--text-muted)'}}>{(a as any).sectionIcon} {(a as any).sectionTitle}</div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* Section accordions (shown when not searching) */}
        {!searchQuery.trim() && DOC_SECTIONS.map(section=>{
          const isOpen = expandedSections.has(section.id);
          return(
            <div key={section.id} style={{...card,padding:0}}>
              {/* Section header */}
              <button
                onClick={()=>toggleSection(section.id)}
                style={{
                  width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'14px 16px',background:'transparent',border:'none',cursor:'pointer',minHeight:52,
                }}
              >
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:20}}>{section.icon}</span>
                  <span style={{fontWeight:700,fontSize:15,color:'var(--text-primary)'}}>{section.title}</span>
                  <span style={{fontSize:11,background:'var(--bg-element)',color:'var(--text-muted)',borderRadius:10,padding:'2px 8px',fontWeight:600}}>
                    {section.articles.length}
                  </span>
                </div>
                <span style={{
                  color:'#5B4FE8',fontSize:18,
                  transform:isOpen?'rotate(180deg)':'rotate(0)',
                  transition:'transform 0.2s',display:'inline-block',
                }}>▾</span>
              </button>

              {/* Articles list with max-height transition */}
              <div style={{
                maxHeight: isOpen ? `${section.articles.length * 64 + 16}px` : '0',
                overflow:'hidden',
                transition:'max-height 0.3s ease',
              }}>
                <div style={{borderTop:'1px solid var(--card-border)',padding:'8px 0 8px'}}>
                  {section.articles.map(article=>{
                    const visited = visitedIds.has(article.id);
                    return(
                      <div
                        key={article.id}
                        onClick={()=>openArticle(article)}
                        style={{
                          display:'flex',alignItems:'center',justifyContent:'space-between',
                          padding:'10px 16px',cursor:'pointer',minHeight:44,
                          background:visited?'rgba(91,79,232,0.04)':'transparent',
                        }}
                      >
                        <div style={{display:'flex',alignItems:'center',gap:10,flex:1}}>
                          <span style={{fontSize:16,flexShrink:0}}>{visited?'✓':'📄'}</span>
                          <span style={{fontSize:14,color:visited?'#818cf8':'var(--text-primary)',fontWeight:visited?600:400}}>
                            {article.title}
                          </span>
                        </div>
                        <span style={{color:'var(--text-faint)',fontSize:16}}>›</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {/* Footer help card */}
        {!searchQuery.trim() && (
          <div style={{borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',padding:20,marginTop:8,textAlign:'center'}}>
            <div style={{fontSize:32,marginBottom:10}}>🆘</div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>Still need help?</div>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16,lineHeight:1.6}}>
              Our team is happy to help you get your bot set up.
            </p>
            <a href={`mailto:${companyEmail}`} style={{...primaryBtn,display:'block',textDecoration:'none',boxSizing:'border-box',marginBottom:10}}>
              ✉️ Email support
            </a>
            <button onClick={()=>navigate('/support')} style={ghostBtn}>💬 Contact support</button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
